/**
 * StatusBadge + TagChips — how data labels (customer statuses, tags) look everywhere.
 *
 * Data colors come from the database (tag.color etc.). We never paint them as
 * solid blocks with white text; instead `softColor()` turns any color into a
 * light tinted background + a darker, readable text color. One rule, every screen.
 *
 * <StatusBadge color="#4ECDC4">ליד חם</StatusBadge>
 *   color     string  optional base color (defaults to neutral gray)
 *   children  node
 *
 * <TagChips tags={[{ name, color, hasActiveSequence, emailSequences }]} max={2} />
 *   tags      array   objects as returned by the customers API (`tagDetails`)
 *   max       number  chips shown before collapsing into "+N" (rest listed in a tooltip)
 *   onRemove  fn(tag, index)  optional — shows an × on each chip (editor use)
 *
 * A chip whose tag has an active email sequence shows a small mail icon; hovering
 * it lists the active sequences (same info as the old 📧 marker).
 */
import React from 'react';
import { Tooltip } from 'antd';
import { MailOutlined, CloseOutlined } from '@ant-design/icons';
import styles from './Badges.css';

const NEUTRAL = '#71717A';

// '#4ECDC4' → { background: 'rgba(78,205,196,0.14)', color: <darkened>, border: 'rgba(…,0.28)' }
export const softColor = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  const value = m ? m[1] : NEUTRAL.slice(1);
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  // Darken toward black so light colors (yellow, mint) stay readable as text.
  const dark = (c) => Math.round(c * 0.55);
  return {
    background: `rgba(${r}, ${g}, ${b}, 0.14)`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.28)`,
    color: `rgb(${dark(r)}, ${dark(g)}, ${dark(b)})`,
  };
};

export const StatusBadge = ({ color, children, className }) => {
  if (!children) return <span className={styles.empty}>—</span>;
  return (
    <span className={[styles.badge, className].filter(Boolean).join(' ')} style={softColor(color)}>
      <span className={styles.dot} />
      {children}
    </span>
  );
};

const sequenceTitle = (tag) =>
  'רצף אימייל פעיל: ' +
  (tag.emailSequences || [])
    .filter((s) => s.isActive)
    .map((s) => s.name)
    .join(', ');

const Chip = ({ tag, onRemove }) => (
  <span className={styles.chip} style={softColor(tag.color)}>
    <span className={styles.chipText}>{tag.name}</span>
    {tag.hasActiveSequence ? (
      <Tooltip title={sequenceTitle(tag)}>
        <MailOutlined className={styles.chipIcon} />
      </Tooltip>
    ) : null}
    {onRemove ? (
      <button type="button" className={styles.chipRemove} onClick={onRemove} aria-label={`הסר ${tag.name}`}>
        <CloseOutlined />
      </button>
    ) : null}
  </span>
);

export const TagChips = ({ tags, max, onRemove, emptyText = '—' }) => {
  const list = tags || [];
  if (list.length === 0) return <span className={styles.empty}>{emptyText}</span>;
  const visible = max ? list.slice(0, max) : list;
  const hidden = max ? list.slice(max) : [];
  return (
    <span className={styles.chips}>
      {visible.map((tag, i) => (
        <Chip key={`${tag.name}-${i}`} tag={tag} onRemove={onRemove ? () => onRemove(tag, i) : null} />
      ))}
      {hidden.length > 0 ? (
        <Tooltip title={hidden.map((t) => t.name).join(', ')}>
          <span className={styles.more}>+{hidden.length}</span>
        </Tooltip>
      ) : null}
    </span>
  );
};
